import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Send, Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { submitContact } from "@/lib/api";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const canSubmit = form.name && form.email && form.message;

    async function onSubmit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await submitContact(form);
            toast.success("Thanks! We'll be in touch shortly.");
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            toast.error("Could not send message. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            data-testid="contact-page"
            className="mx-auto max-w-7xl px-4 pb-24 pt-16 sm:px-6 lg:px-8"
        >
            <div className="grid gap-12 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                        Contact
                    </div>
                    <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-[color:var(--brand-navy)] sm:text-5xl">
                        Get in touch
                    </h1>
                    <p className="mt-5 text-slate-600">
                        Questions about a booking, delivery, or a special
                        request? We&apos;d love to help.
                    </p>
                    <ul className="mt-8 space-y-6 text-sm">
                        <li className="flex items-start gap-4">
                            <span className="grid h-10 w-10 place-items-center rounded-xl bg-[color:var(--brand-navy)] text-white">
                                <MapPin className="h-4 w-4" />
                            </span>
                            <div>
                                <div className="font-semibold text-[color:var(--brand-navy)]">
                                    Office
                                </div>
                                <div className="text-slate-600">
                                    924 N Magnolia Avenue, Suite 202
                                    <br />
                                    Orlando, FL 32803
                                </div>
                            </div>
                        </li>
                        <li className="flex items-start gap-4">
                            <span className="grid h-10 w-10 place-items-center rounded-xl bg-[color:var(--brand-gold)] text-white">
                                <Mail className="h-4 w-4" />
                            </span>
                            <div>
                                <div className="font-semibold text-[color:var(--brand-navy)]">
                                    Email
                                </div>
                                <a
                                    href="mailto:info@cflmobilityrentals.com"
                                    data-testid="contact-email-link"
                                    className="text-slate-600 hover:text-[color:var(--brand-green)]"
                                >
                                    info@cflmobilityrentals.com
                                </a>
                            </div>
                        </li>
                    </ul>
                </div>

                <form
                    onSubmit={onSubmit}
                    data-testid="contact-form"
                    className="card-surface p-8 lg:col-span-7"
                >
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <Label className="mb-2 block text-sm font-medium text-slate-700">
                                Name
                            </Label>
                            <Input
                                data-testid="contact-name-input"
                                value={form.name}
                                onChange={set("name")}
                                className="h-12 rounded-xl"
                                required
                            />
                        </div>
                        <div>
                            <Label className="mb-2 block text-sm font-medium text-slate-700">
                                Phone
                            </Label>
                            <Input
                                data-testid="contact-phone-input"
                                value={form.phone}
                                onChange={set("phone")}
                                className="h-12 rounded-xl"
                            />
                        </div>
                    </div>
                    <div className="mt-4">
                        <Label className="mb-2 block text-sm font-medium text-slate-700">
                            Email
                        </Label>
                        <Input
                            data-testid="contact-email-input"
                            type="email"
                            value={form.email}
                            onChange={set("email")}
                            className="h-12 rounded-xl"
                            required
                        />
                    </div>
                    <div className="mt-4">
                        <Label className="mb-2 block text-sm font-medium text-slate-700">
                            Message
                        </Label>
                        <Textarea
                            data-testid="contact-message-input"
                            value={form.message}
                            onChange={set("message")}
                            rows={6}
                            className="rounded-xl"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        data-testid="contact-submit-button"
                        disabled={!canSubmit || submitting}
                        className="hero-cta pill-btn mt-6 inline-flex items-center gap-2 text-white disabled:opacity-60"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Sending…
                            </>
                        ) : (
                            <>
                                Send message <Send className="h-4 w-4" />
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
